import { useCallback, useEffect, useState } from "react";
import { Card, Gate, Head, State } from "@authoxi/ui-shared";
import { type Whois as Answer, client, whois } from "../api";

/**
 * Resolve one identifier.
 *
 * Five-plus answer states, not found/not-found. "That is a key thumbprint, which is not a
 * global identifier" and "only did:key is resolvable here" are real answers and better ones
 * than a shrug — and "not in this index" is emphatically **not** "fake", which is the single
 * confident-wrong-answer this tool must never give.
 *
 * The query is in the URL so a result is a link somebody can send.
 */
export function Whois() {
  const initial = new URLSearchParams(location.search).get("q") ?? "";
  const [q, setQ] = useState(initial);
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [busy, setBusy] = useState(false);

  const run = useCallback(async (query: string) => {
    if (!query.trim()) return;
    setBusy(true);
    try {
      setAnswer(await whois(query.trim()));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (initial) void run(initial);
  }, [initial, run]);

  return (
    <>
      <Head
        pill="Agent whois"
        title="Who published this did?"
        lede="You have a signature and a key it names. This tells you who put that key on the internet — the HIBP-shaped question for agent identity."
      />

      <form
        className="tool-input"
        data-testid="whois-form"
        onSubmit={(e) => {
          e.preventDefault();
          // Keep the query in the URL, so the answer is shareable and the back button works.
          history.replaceState(null, "", `/whois?q=${encodeURIComponent(q)}`);
          void run(q);
        }}
      >
        <input
          className="input input-mono"
          data-testid="whois-q"
          placeholder="did:key:z6Mk… · agt_… · a hostname"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button className="btn btn-go btn-lg" type="submit" disabled={busy}>
          {busy ? "…" : "Resolve"}
        </button>
      </form>

      <div style={{ marginTop: "var(--s5)" }}>
        {busy && <State kind="loading" title="Resolving…" />}
        {!busy && answer && <Answer answer={answer} />}
      </div>
    </>
  );
}

function Answer({ answer }: { answer: Answer }) {
  const facts = Object.entries(answer.facts);
  const links = Object.entries(answer.links);

  return (
    <div className="result" data-testid="whois-result">
      <Card pad={false}>
        <div className="receipt-head">
          <div className="row">
            <div>
              <p className="label" data-testid="whois-kind">
                {answer.found ? "Resolved" : answer.query_type}
              </p>
              <h2 className="md" style={{ margin: "2px 0 0" }}>
                {answer.summary}
              </h2>
            </div>
          </div>
        </div>

        {/* Rendered generically from `facts` and `links`, both dicts. Filling them out on the
            server needs no change here — which is how the host branch went from one fact to
            four without touching this file. */}
        {facts.length > 0 && (
          <dl style={{ margin: 0 }}>
            {facts.map(([k, v]) => (
              <div className="receipt-row" key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        )}
      </Card>

      {answer.note && (
        <Card pad className="">
          <p className="sm" style={{ margin: 0, color: "var(--ink-2)", maxWidth: "var(--measure)" }}>
            {answer.note}
          </p>
        </Card>
      )}

      {links.length > 0 && (
        <div className="row wrap" style={{ marginTop: "var(--s4)" }}>
          {links.map(([k, url]) => (
            <a className="btn" href={url} key={k} rel="external">
              {k}
            </a>
          ))}
        </div>
      )}

      {answer.found && (
        <Gate
          client={client}
          source="whois"
          subject={answer.query}
          title="Tell me about this identity"
          body="We will write once if anything about this record changes. Useful if you are about to depend on it."
          cta="Keep me posted"
        />
      )}
    </div>
  );
}
